import i18n from '@/i18n';
import { imageSpaceAPI, sourcingAPI } from '@/utils/api';
import { normalizePersistedImageUrl } from '@/utils/imageUrlUtils';
import { ensureDisplayImageUrls, warmDisplayCacheFromRecords } from '@/utils/imageDisplayAccess';
import { navigateWorkbenchWithState } from '@/pages/ipe/workbench/constants/workbenchNavConfig';
import {
  getSourcingProductPrimaryImage,
  getSourcingProductPrimaryImageViewUrl,
  getSourcingImageSpaceRecordId,
  getSourcingImageSpaceUrl,
} from './sourcingProductDisplay';

const MODEL_SLOT_BY_NAV = {
  modelTryOn: 'garment',
  modelHandheld: 'product',
  modelPoseFission: 'model',
  modelUniversalWear: 'garment',
};

const CROP_SOURCE_NAV_IDS = new Set(['imageCrop', 'imageEnhance', 'smartCutout']);

const AI_IMAGE_PREFILL_NAV_IDS = new Set([
  'aiOutpaint',
  'stampExtract',
  'aiTextToImage',
  'aiCopywriting',
]);

/** 快捷联动：新标签打开目标模块，避免挤掉当前详情页 */
function navigateWorkbenchTool(navigate, navId, extraState = {}, options = {}) {
  navigateWorkbenchWithState(navigate, navId, extraState, { ...options, openInNewTab: true });
}

/**
 * 历史批次可能未写入 imageSpaceRecordId：按 linkedSourcingBatchKey + externalKey 从图片空间补全。
 */
export async function enrichSourcingRowsWithImageSpace(rows, batchKey) {
  const list = Array.isArray(rows) ? rows : [];
  const bk = String(batchKey || '').trim();
  if (!bk || list.length === 0) {
    return list;
  }
  if (list.every((row) => getSourcingImageSpaceRecordId(row) && getSourcingImageSpaceUrl(row))) {
    return list;
  }
  try {
    // B3：禁止 pageSize=5000；仅首屏页用于补齐 sourcing 映射
    const res = await imageSpaceAPI.getAll({ page: 1, pageSize: 50 });
    const images = Array.isArray(res?.images) ? res.images : [];
    const byExternalKey = new Map();
    for (const img of images) {
      const linked = String(img.linkedSourcingBatchKey ?? img.linked_sourcing_batch_key ?? '').trim();
      if (linked !== bk) continue;
      const externalKey = String(img.externalKey ?? img.external_key ?? '').trim();
      if (!externalKey || byExternalKey.has(externalKey)) continue;
      byExternalKey.set(externalKey, img);
    }
    if (byExternalKey.size === 0) {
      return list;
    }
    return list.map((row) => {
      if (getSourcingImageSpaceRecordId(row) && getSourcingImageSpaceUrl(row)) {
        return row;
      }
      const externalKey = String(row.externalKey ?? row.external_key ?? '').trim();
      const img = externalKey ? byExternalKey.get(externalKey) : null;
      if (!img) return row;
      const url = String(img.url ?? img.src ?? '').trim();
      if (!url || !img.id) return row;
      return {
        ...row,
        imageSpaceRecordId: String(img.id),
        imageSpaceUrl: url,
      };
    });
  } catch {
    return list;
  }
}

/** 从采集条目构建可预填的图片源（优先使用已同步的图片空间记录） */
export function buildSourcingBridgeSources(rows) {
  return (rows || [])
    .map((row) => {
      const imageSpaceId = getSourcingImageSpaceRecordId(row);
      const imageSpaceUrl = getSourcingImageSpaceUrl(row);
      const previewUrl = getSourcingProductPrimaryImageViewUrl(row) || imageSpaceUrl;
      const name = String(row.title || row.listingText || imageSpaceId || row.id || '').trim().slice(0, 120)
        || String(row.id ?? '');

      if (imageSpaceId && imageSpaceUrl) {
        return {
          id: imageSpaceId,
          name,
          url: imageSpaceUrl,
          previewUrl: previewUrl || imageSpaceUrl,
          sourceRow: row,
          fromImageSpace: true,
        };
      }

      const rawUrl = getSourcingProductPrimaryImage(row);
      const persisted = normalizePersistedImageUrl(rawUrl) || String(rawUrl || '').trim();
      const url = String(persisted || '').trim();
      if (!url) return null;
      return {
        id: String(row.id ?? row.externalKey ?? `sourcing-${Date.now()}`),
        name,
        url,
        previewUrl: previewUrl || url,
        sourceRow: row,
        fromImageSpace: false,
      };
    })
    .filter(Boolean);
}

function collectSourcingTitles(rows) {
  return (rows || [])
    .map((row) => String(row.title || row.listingText || '').trim())
    .filter(Boolean);
}

function buildImageSpaceUrlMap(sources) {
  const urlMap = {};
  for (const s of sources) {
    if (s?.id && s?.url) {
      urlMap[String(s.id)] = s.url;
    }
  }
  return urlMap;
}

async function prefetchImageToolSources(sources) {
  const list = Array.isArray(sources) ? sources : [];
  if (!list.length) return;
  const records = list
    .map((s) => {
      const url = normalizePersistedImageUrl(s?.url) || String(s?.url || '').trim();
      const preview = String(s?.previewUrl || s?.viewUrl || s?.displayUrl || '').trim();
      if (!url || !preview) return null;
      return {
        id: s.id,
        url,
        viewUrl: preview,
        displayUrl: preview,
      };
    })
    .filter(Boolean);
  if (records.length) {
    // warmDisplayCacheFromRecords 会拒绝 signed-only 下的未预签名 OSS 裸链
    warmDisplayCacheFromRecords(records, (item) => item.url);
  }
  const urls = list
    .map((s) => normalizePersistedImageUrl(s?.url) || String(s?.url || '').trim())
    .filter(Boolean);
  if (urls.length) {
    await ensureDisplayImageUrls(urls);
  }
}

/** 工具页预填：canonical url + 可选已签名 previewUrl */
export function normalizeImageToolPrefillSource(source) {
  if (!source?.url) return null;
  const raw = String(source.url).trim();
  if (!raw) return null;
  const url = normalizePersistedImageUrl(raw) || raw;
  const previewUrl = String(source.previewUrl || source.viewUrl || source.displayUrl || '').trim();
  return {
    id: source.id,
    name: source.name || source.id || '原图',
    url,
    previewUrl: previewUrl || undefined,
    viewUrl: previewUrl || undefined,
    displayUrl: previewUrl || undefined,
  };
}

/**
 * 通用：选中图片源 → 跳转智能工具（只带入图片与参数，不创建任务、不自动执行）
 * @param {{ navId: string, sources: {id:string,name:string,url:string,previewUrl?:string,fromImageSpace?:boolean}[], navigate: Function, onToast?: Function, titlePrompt?: string, language?: string, studioParams?: object, modelStudioParams?: object, podSelectedTemplates?: object[] }} params
 */
export async function navigateImageSourcesToTool({
  navId,
  sources: rawSources,
  navigate,
  onToast,
  titlePrompt = '',
  language = '',
  studioParams = null,
  modelStudioParams = null,
  podSelectedTemplates = null,
}) {
  const sources = (Array.isArray(rawSources) ? rawSources : [])
    .map((s) => normalizeImageToolPrefillSource(s))
    .filter(Boolean);
  if (!sources.length) {
    onToast?.(i18n.t('workbench:ipeUtilError.selectImageFirstShort'), 'warning');
    return;
  }

  await prefetchImageToolSources(sources);

  const prompt = String(titlePrompt || '').trim().slice(0, 500);

  if (navId === 'platformListing') {
    onToast?.(i18n.t('workbench:ipeUtilError.sendFromCollectDetail'), 'warning');
    return;
  }

  if (navId === 'platformListingJobs') {
    navigateWorkbenchTool(navigate, 'platformListingJobs');
    return;
  }

  if (navId === 'infringementDetection') {
    navigateWorkbenchTool(navigate, 'infringementDetection', {
      openCreate: true,
      imageUrls: sources.map((s) => s.url),
    });
    onToast?.(i18n.t('workbench:ipeUtilError.carriedImages', { count: sources.length }), 'success');
    return;
  }

  if (navId === 'imageFission') {
    navigateWorkbenchTool(navigate, 'imageFission', {
      openCreate: true,
      fissionSources: sources,
    });
    onToast?.(i18n.t('workbench:ipeUtilError.carriedSources', { count: sources.length }), 'success');
    return;
  }

  if (CROP_SOURCE_NAV_IDS.has(navId)) {
    navigateWorkbenchTool(navigate, navId, {
      openCreate: true,
      cropSources: sources,
    });
    onToast?.(i18n.t('workbench:ipeUtilError.carryingImages', { count: sources.length }), 'info');
    return;
  }

  if (AI_IMAGE_PREFILL_NAV_IDS.has(navId)) {
    const prefill = {
      navId,
      sources: sources.map((s) => ({
        id: s.id,
        name: s.name,
        url: s.url,
        previewUrl: s.previewUrl || s.url,
        viewUrl: s.viewUrl || s.previewUrl,
        displayUrl: s.displayUrl || s.previewUrl,
      })),
    };
    if (navId === 'aiTextToImage' && prompt) prefill.prompt = prompt;
    if (navId === 'aiCopywriting' && prompt) prefill.prompt = prompt.split('\n')[0]?.slice(0, 500);
    if (navId === 'aiCopywriting' && language) {
      prefill.language = String(language).trim();
    }
    navigateWorkbenchTool(navigate, navId, {
      openCreate: true,
      aiImageStudioPrefill: prefill,
    });
    onToast?.(i18n.t('workbench:ipeUtilError.carriedImages', { count: sources.length }), 'success');
    return;
  }

  const modelSlot = MODEL_SLOT_BY_NAV[navId];
  if (modelSlot) {
    const items = sources.map((s) => ({
      id: s.id,
      name: s.name,
      previewUrl: s.previewUrl || s.url,
      apiUrl: s.url,
    }));
    const extraParams = modelStudioParams && typeof modelStudioParams === 'object'
      ? modelStudioParams
      : {};
    const promptFromParams = String(extraParams.prompt || '').trim();
    navigateWorkbenchTool(navigate, navId, {
      openCreate: true,
      modelStudioPrefill: {
        navId,
        slots: { [modelSlot]: items },
        prompt: promptFromParams || prompt || undefined,
        ...(extraParams.generateCount != null ? { generateCount: extraParams.generateCount } : {}),
        ...(extraParams.modelType ? { modelType: extraParams.modelType } : {}),
        ...(extraParams.modelEthnicity ? { modelEthnicity: extraParams.modelEthnicity } : {}),
        ...(extraParams.garmentType ? { garmentType: extraParams.garmentType } : {}),
        ...(extraParams.frameScope ? { frameScope: extraParams.frameScope } : {}),
      },
    });
    onToast?.(i18n.t('workbench:ipeUtilError.carriedImages', { count: sources.length }), 'success');
    return;
  }

  if (navId === 'videos') {
    navigateWorkbenchTool(navigate, 'videos', {
      openCreate: true,
      videoStudioPrefill: { sources },
    });
    onToast?.(i18n.t('workbench:ipeUtilError.carriedRefs', { count: sources.length }), 'success');
    return;
  }

  if (navId === 'podProductDesign') {
    const templates = Array.isArray(podSelectedTemplates)
      ? podSelectedTemplates
      : (Array.isArray(studioParams?.selectedTemplates) ? studioParams.selectedTemplates : null);
    navigateWorkbenchTool(navigate, 'podProductDesign', {
      openCreate: true,
      podStampSources: sources,
      ...(templates?.length ? { podSelectedTemplates: templates } : {}),
    });
    onToast?.(i18n.t('workbench:ipeUtilError.carriedStamps', { count: sources.length }), 'success');
    return;
  }

  if (navId === 'goods') {
    const usable = sources.filter((s) => s?.url);
    if (usable.length === 0) {
      onToast?.(i18n.t('workbench:ipeUtilError.selectImagesFirst'), 'warning');
      return;
    }
    const prefillSources = usable.map((s) => ({
      id: String(s.id || `goods-${Date.now()}`),
      name: s.name || String(s.id || '印花'),
      url: s.url,
      previewUrl: s.previewUrl || s.url,
      viewUrl: s.viewUrl || s.previewUrl,
      displayUrl: s.displayUrl || s.previewUrl,
    }));
    navigateWorkbenchTool(navigate, 'goods', {
      openCreate: true,
      goodsStudioPrefill: {
        sources: prefillSources,
        ...(studioParams && typeof studioParams === 'object' ? { studioParams } : {}),
      },
    });
    onToast?.(i18n.t('workbench:ipeUtilError.carriedImages', { count: usable.length }), 'success');
    return;
  }

  if (navId === 'img2img') {
    const linked = sources.filter((s) => s.fromImageSpace && s.id && s.url);
    const usable = linked.length > 0
      ? linked
      : sources.filter((s) => s?.id && s?.url);
    if (usable.length === 0) {
      onToast?.(
        linked.length === 0 && sources.some((s) => s?.url)
          ? '请先选择可用图片'
          : i18n.t('workbench:ipeUtilError.spaceUnlinked'),
        'warning',
      );
      return;
    }
    if (linked.length > 0 && linked.length < sources.length) {
      onToast?.(i18n.t('workbench:ipeUtilError.skippedUnlinked', { count: sources.length - linked.length }), 'warning');
    }
    const ids = usable.map((s) => String(s.id));
    const urlMap = buildImageSpaceUrlMap(usable);
    navigateWorkbenchTool(navigate, 'img2img', {
      openCreate: true,
      imageIds: ids,
      img2imgRefUrls: urlMap,
    });
    onToast?.(
      linked.length > 0
        ? i18n.t('workbench:ipeUtilError.usedSpaceImages', { count: usable.length })
        : i18n.t('workbench:ipeUtilError.carriedImages', { count: usable.length }),
      'success',
    );
    return;
  }

  navigateWorkbenchTool(navigate, navId);
}

/** 采集快捷弹窗 sources 上挂的原条目（插件采集送入商品库时复用，避免再入库一份） */
export function collectSourcingRowsFromBridgeSources(sources) {
  const seen = new Set();
  const rows = [];
  for (const source of Array.isArray(sources) ? sources : []) {
    const row = source?.sourceRow;
    const id = row?.id;
    if (id == null || seen.has(String(id))) continue;
    seen.add(String(id));
    rows.push(row);
  }
  return rows;
}

/**
 * 将已有采集条目晋级到商品库（不新建批次）。
 * @returns {Promise<{ batchKey: string, ingestedRows: object[], count: number }>}
 */
export async function promoteSourcingRowsToPlatformListing(rows, callbacks = {}) {
  return promoteSourcingRowsToPlatformListingProgressive(rows, callbacks);
}

/**
 * 渐进式晋级：先提示批次，再逐条 promote，本地商品库可轮询进度。
 * @param {object[]} rows
 * @param {object} [callbacks]
 * @returns {Promise<{ batchKey: string, ingestedRows: object[], count: number }>}
 */
export async function promoteSourcingRowsToPlatformListingProgressive(rows, callbacks = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const ids = list.map((row) => row.id).filter((id) => id != null);
  if (ids.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.entryUnrecognized'));
  }
  const batchKey = String(list[0]?.batchKey || list[0]?.batch_key || '').trim();
  if (!batchKey) {
    throw new Error(i18n.t('workbench:ipeUtilError.batchUnrecognized'));
  }

  callbacks.onBatchReady?.({
    batchKey,
    expectedCount: ids.length,
    mode: 'promote',
    sourceLabel: '素材采集',
  });

  const {
    bindListingAutoRule = false,
    listingAutoRuleId = null,
  } = callbacks;

  const ingestedRows = [];
  for (let index = 0; index < ids.length; index += 1) {
    const id = ids[index];
    const promoteOptions = (index === 0 && bindListingAutoRule)
      ? { bindListingAutoRule: true, listingAutoRuleId }
      : {};
    await sourcingAPI.promoteItems([id], promoteOptions);
    const row = list.find((item) => String(item?.id) === String(id)) || list[index];
    if (row) ingestedRows.push(row);
    callbacks.onProgress?.({
      batchKey,
      done: index + 1,
      total: ids.length,
      ingestedRows: [...ingestedRows],
    });
  }

  try {
    const { preferDownstreamAssetsForSourcingListingRows } = await import(
      './preferDownstreamListingAssets'
    );
    await preferDownstreamAssetsForSourcingListingRows(list);
  } catch (enrichError) {
    console.warn('[sourcingToolBridge] 下游成果优先覆盖失败', enrichError);
  }

  const result = {
    batchKey,
    ingestedRows,
    count: ids.length,
  };
  callbacks.onComplete?.(result);
  return result;
}

/**
 * 采集/分析详情：选中商品 → 跳转智能工具并带入主图（直接使用图片空间已同步记录）
 * @param {{ navId: string, rows: object[], batchKey?: string, navigate: Function, onToast?: (msg: string, type?: string) => void }} params
 */
export async function navigateSourcingToTool({
  navId,
  rows,
  batchKey,
  navigate,
  onToast,
  resolveForIngest,
}) {
  if (navId === 'platformListing') {
    try {
      const autoRuleChoice = typeof resolveForIngest === 'function'
        ? await resolveForIngest()
        : { proceed: true, listingAutoRuleId: null, bindListingAutoRule: false };
      if (!autoRuleChoice?.proceed) return;

      const {
        markListingIngestPending,
        clearListingIngestPending,
        completeListingIngestPending,
        emitListingIngestProgress,
        updateListingIngestPendingProgress,
      } = await import('./listingIngestProgress');
      const { showPlatformListingIngestStartedToast } = await import('./showTaskCreatedDetailToast');
      let activeBatchKey = '';
      await promoteSourcingRowsToPlatformListingProgressive(rows, {
        bindListingAutoRule: !!autoRuleChoice.bindListingAutoRule,
        listingAutoRuleId: autoRuleChoice.listingAutoRuleId ?? null,
        onBatchReady: ({ batchKey: bk, expectedCount }) => {
          activeBatchKey = bk;
          markListingIngestPending(bk, {
            mode: 'promote',
            total: expectedCount,
            sourceLabel: '素材采集',
          });
          showPlatformListingIngestStartedToast({ navigate, batchKey: bk });
        },
        onProgress: ({ batchKey: bk, done, total }) => {
          updateListingIngestPendingProgress(bk || activeBatchKey, done, total);
          emitListingIngestProgress(bk || activeBatchKey);
        },
        onComplete: ({ batchKey: bk, count, ingestedRows }) => {
          const key = bk || activeBatchKey;
          completeListingIngestPending(key, Number(count) || 0);
          emitListingIngestProgress(key);
          const ruleIds = Array.isArray(autoRuleChoice.listingAutoRuleIds)
            ? autoRuleChoice.listingAutoRuleIds
            : (autoRuleChoice.listingAutoRuleId != null ? [autoRuleChoice.listingAutoRuleId] : []);
          if (autoRuleChoice.bindListingAutoRule && ruleIds.length > 0) {
            const sourceItems = (Array.isArray(ingestedRows) ? ingestedRows : rows)
              .filter((row) => row?.id != null);
            void import('./publishItemsWithListingAutoRules').then(({ publishExtraAutoRulesAfterIngest }) => (
              publishExtraAutoRulesAfterIngest({
                items: sourceItems,
                sourcingItemIds: sourceItems.map((row) => row.id),
                listingAutoRuleIds: ruleIds,
                primaryRuleId: ruleIds[0] ?? autoRuleChoice.listingAutoRuleId ?? null,
                remarkPrefix: '素材采集多规则上架',
                batchKey: key,
              })
            ));
          }
        },
      });
    } catch (e) {
      onToast?.(e.message || i18n.t('workbench:ipeUtilError.sendToGoodsFailed'), 'error');
    }
    return;
  }

  const enriched = await enrichSourcingRowsWithImageSpace(rows, batchKey);
  const sources = buildSourcingBridgeSources(enriched);
  if (sources.length === 0) {
    onToast?.(i18n.t('workbench:ipeUtilError.noUsableMainImage'), 'warning');
    return;
  }

  const titlePrompt = collectSourcingTitles(enriched).join('\n').slice(0, 500);
  await navigateImageSourcesToTool({
    navId,
    sources,
    navigate,
    onToast,
    titlePrompt,
  });
}
